import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import type { Recording } from "../../api/types";
import { splitApiRequestFormErrors } from "../../api";

export default function SessionRenameDialog({
  recording,
  onRename,
  onClose,
}: {
  recording: Recording;
  onRename: (id: string, name: string | null) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(recording.name ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buttonClass = "h-9 rounded-lg border border-outline px-3 text-sm disabled:opacity-50";
  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !saving) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[111] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-outline bg-surface p-5 text-text shadow-xl"
        >
          <Dialog.Title className="text-lg font-semibold">{t("rename_recording")}</Dialog.Title>
          <form
            onSubmit={async event => {
              event.preventDefault();
              if (saving) return;
              setSaving(true);
              setError(null);
              try {
                await onRename(recording.id, value.trim() || null);
                onClose();
              } catch (err) {
                const parsed = splitApiRequestFormErrors<{ name: string }>(err, t, ["name"]);
                setError(parsed.fieldErrors.name ?? parsed.remainder);
              } finally {
                setSaving(false);
              }
            }}
          >
            <label className="mt-4 block text-sm">
              {t("activity.session_name")}
              <input
                autoFocus
                value={value}
                onChange={event => setValue(event.target.value)}
                disabled={saving}
                className="mt-2 h-10 w-full rounded-lg border border-outline bg-surface px-3 focus-visible:outline-info"
              />
            </label>
            {error && (
              <p role="alert" className="mt-2 text-sm text-danger">
                {error}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Dialog.Close asChild>
                <button type="button" disabled={saving} className={buttonClass}>
                  {t("cancel")}
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={saving}
                className={`${buttonClass} border-info bg-info text-white`}
              >
                {t(saving ? "loading_ellipsis" : "save")}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
